// API client สำหรับ Notification Platform (แจ้งเตือน In-App / Web Push / Telegram)
// Endpoint/shape อ้างอิงจาก thaiusinvestment-api (develop, notification-platform.md)
// ใช้ token เดียวกับที่เหลือทั้งแอป (getToken) — สิทธิ์ admin เช็คจาก isAdmin ของบัญชีฝั่ง backend เอง
import { API_URL, handleAuthInvalidToken } from './fetchAPI';
import { getToken } from './tokenManager';

export type NotificationCategory =
  | 'ARTICLE' | 'ANNOUNCEMENT' | 'PRICE_ALERT' | 'TRADE' | 'DIVIDEND'
  | 'PORTFOLIO' | 'MEMBERSHIP' | 'MARKET' | 'SYSTEM' | 'SECURITY';
export type NotificationChannel = 'IN_APP' | 'WEB_PUSH' | 'TELEGRAM';
export type NotificationPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
export type NotificationStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHED' | 'CANCELLED' | 'EXPIRED';
export type AudienceType = 'INDIVIDUAL' | 'ALL';

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  'ARTICLE', 'ANNOUNCEMENT', 'PRICE_ALERT', 'TRADE', 'DIVIDEND',
  'PORTFOLIO', 'MEMBERSHIP', 'MARKET', 'SYSTEM', 'SECURITY',
];
export const NOTIFICATION_CHANNELS: NotificationChannel[] = ['IN_APP', 'WEB_PUSH', 'TELEGRAM'];
export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  ARTICLE: 'บทความ', ANNOUNCEMENT: 'ประกาศ', PRICE_ALERT: 'ราคา', TRADE: 'ซื้อขาย',
  DIVIDEND: 'ปันผล', PORTFOLIO: 'พอร์ต', MEMBERSHIP: 'สมาชิก', MARKET: 'ตลาด',
  SYSTEM: 'ระบบ', SECURITY: 'ความปลอดภัย',
};

// ---------- Category / Type taxonomy ----------
// อ้างอิง Notification Category & Type Guide (develop @ 2026-09-23)
// Category = "เรื่องอะไร", Type = "เกิดเหตุการณ์อะไร" — API รับครบ 10 หมวด แต่มี canonical type จริงแค่บางหมวด

export const NOTIFICATION_TYPES: Record<NotificationCategory, string[]> = {
  ARTICLE: ['ARTICLE_PUBLISHED'],
  ANNOUNCEMENT: ['ADMIN_ANNOUNCEMENT', 'IMPORTANT_NOTICE', 'BRAKING_NEWS', 'LIVE_REMINDER', 'LIVE_STARTED'],
  PORTFOLIO: ['REALTIME_PUBLISHED'],
  SYSTEM: [
    'MAINTENANCE_SCHEDULED', 'MAINTENANCE_STARTED', 'MAINTENANCE_COMPLETED',
    'SERVICE_DEGRADED', 'SERVICE_RECOVERED', 'PATCH_NOTES_PUBLISHED',
  ],
  SECURITY: ['SECURITY_LOGIN_ALERT'],
  // ยังไม่มี canonical type/producer — API รับได้แต่ยังไม่เปิดใช้
  PRICE_ALERT: [], TRADE: [], DIVIDEND: [], MEMBERSHIP: [], MARKET: [],
};

// หมวดที่มี type จริงแล้ว — ใช้กับ filter feed / ตาราง preference เพื่อไม่โชว์หมวดที่ยังไม่มีแจ้งเตือนเกิดขึ้นได้
export const LIVE_NOTIFICATION_CATEGORIES: NotificationCategory[] =
  NOTIFICATION_CATEGORIES.filter((c) => NOTIFICATION_TYPES[c].length > 0);

// หมวดที่ admin สร้างเองผ่าน /admin/notifications ได้ ที่เหลือมี producer ของตัวเอง:
// ARTICLE → POST /articles/:id/published, PORTFOLIO → POST /realtime-v2/:id/publish, SECURITY → ระบบส่งเอง
export const ADMIN_COMPOSABLE_CATEGORIES: NotificationCategory[] = ['ANNOUNCEMENT', 'SYSTEM'];
export const PRODUCER_OWNED_CATEGORY_HINT: Partial<Record<NotificationCategory, string>> = {
  ARTICLE: 'ส่งจากหน้าบทความ',
  PORTFOLIO: 'ส่งจากหน้า Realtime',
  SECURITY: 'ระบบส่งอัตโนมัติ',
};

// legacy alias → type ปัจจุบัน (ของเก่ายังอยู่ใน feed ได้ แต่งานใหม่ใช้ type ใหม่)
// BRAKING_NEWS สะกดตาม backend (ไม่ใช่ BREAKING) — map ตัวสะกดถูกไว้ด้วยเผื่อ backend แก้ทีหลัง
const TYPE_ALIASES: Record<string, string> = {
  IMPORTANT_ANNOUNCEMENT: 'IMPORTANT_NOTICE',
  BREAKING_NEWS: 'BRAKING_NEWS',
};

export interface TypePresentation {
  label: string; // semantic label ที่ขึ้นเหนือหัวข้อ
  typeLabel: string; // ชื่อ type สำหรับ admin เลือก
  cta: string; // ข้อความปุ่มเมื่อมี deepLink
}

export const NOTIFICATION_TYPE_PRESENTATION: Record<string, TypePresentation> = {
  ARTICLE_PUBLISHED: { label: '📰 บทความใหม่', typeLabel: 'บทความใหม่', cta: 'อ่านบทความ' },
  ADMIN_ANNOUNCEMENT: { label: '📣 ประกาศจากทีมงาน', typeLabel: 'ประกาศทั่วไป', cta: 'ดูรายละเอียด' },
  IMPORTANT_NOTICE: { label: '⚠️ ประกาศสำคัญจากทีมงาน', typeLabel: 'ประกาศสำคัญ', cta: 'ดูรายละเอียด' },
  BRAKING_NEWS: { label: '🚨 ข่าวด่วน', typeLabel: 'ข่าวด่วน (Breaking news)', cta: 'อ่านข่าว' },
  LIVE_REMINDER: { label: '📺 Live', typeLabel: 'Live ที่ยังไม่เริ่ม', cta: 'ดูรายละเอียด Live' },
  LIVE_STARTED: { label: '🔴 LIVE', typeLabel: 'Live เริ่มแล้ว', cta: 'ดู Live ตอนนี้' },
  REALTIME_PUBLISHED: { label: '📊 Realtime', typeLabel: 'Realtime snapshot', cta: 'ดู Realtime' },
  MAINTENANCE_SCHEDULED: { label: '🛠️ System Update', typeLabel: 'แจ้งล่วงหน้าปิดปรับปรุง', cta: 'ดูรายละเอียด' },
  MAINTENANCE_STARTED: { label: '🛠️ กำลังปรับปรุงระบบ', typeLabel: 'เริ่มปิดปรับปรุง', cta: 'ดูสถานะระบบ' },
  MAINTENANCE_COMPLETED: { label: '✅ ระบบกลับมาใช้งานได้แล้ว', typeLabel: 'ปรับปรุงเสร็จ', cta: 'ดูรายละเอียด' },
  SERVICE_DEGRADED: { label: '⚠️ System Update', typeLabel: 'บริการช้า/ผิดปกติ', cta: 'ดูสถานะระบบ' },
  SERVICE_RECOVERED: { label: '✅ ระบบกลับมาเป็นปกติแล้ว', typeLabel: 'บริการกลับมาปกติ', cta: 'ดูรายละเอียด' },
  PATCH_NOTES_PUBLISHED: { label: '✨ มีอะไรใหม่ใน WeThaiInvest', typeLabel: 'Patch notes', cta: 'ดูรายละเอียดอัปเดต' },
  SECURITY_LOGIN_ALERT: { label: '🔐 ความปลอดภัยบัญชี', typeLabel: 'แจ้งเตือนการเข้าสู่ระบบ', cta: 'ตรวจสอบบัญชี' },
};

// ข่าวด่วนใช้ UI แยก (ไล่สีเขียว) ทั้ง toast และรายการในกล่องแจ้งเตือน
export function isBreakingNews(type: string): boolean {
  return (TYPE_ALIASES[type] ?? type) === 'BRAKING_NEWS';
}
// lime → emerald → teal ใช้กับกรอบ toast / แถบซ้ายของรายการ / ป้าย "ข่าวด่วน"
export const BREAKING_NEWS_GRADIENT = 'linear-gradient(120deg, #A3E635 0%, #22C55E 48%, #0D9488 100%)';
export const BREAKING_NEWS_INK = '#052E16'; // ตัวอักษรบนพื้นไล่สี

// unknown type → ใช้ชื่อหมวดแทน (ตาม rule "Unknown type ใช้ default ของ category")
// ใช้ได้เฉพาะตอนไม่มี NotificationItem จริง (เช่น admin draft ก่อน publish) — ถ้ามี item จาก feed แล้ว
// ให้ใช้ resolvePresentation(item) แทน เพราะ backend ส่ง presentation จริงมาให้แล้ว ไม่ต้องเดาเอง
export function getTypePresentation(category: NotificationCategory, type: string): TypePresentation {
  const known = NOTIFICATION_TYPE_PRESENTATION[TYPE_ALIASES[type] ?? type];
  if (known) return known;
  const label = NOTIFICATION_CATEGORY_LABELS[category] || category;
  return { label, typeLabel: type, cta: 'ดูรายละเอียด' };
}

// backend ส่ง semantic presentation มากับ item ของ feed จริงแล้ว (ดู notification-presentation.md /
// Category & Type Guide 09 · CHANNEL RENDERING: "Frontend ไม่ควรเดา type จากข้อความ title/body ให้ render
// จาก semantic presentation fields ที่ backend/registry ส่งมา") — field ที่ยืนยันจาก response จริงคือ
// icon/label/tone/layout/bodyStyle รวมถึง primaryAction/primaryActionLabel ก็อยู่ *ใน* presentation ด้วย
// (ไม่ใช่ top-level ของ item อย่างที่เข้าใจผิดตอนแรก) — เก็บ top-level ไว้เป็น fallback เฉยๆ เผื่อ backend
// เปลี่ยน shape ในอนาคต ค่าอื่นที่ backend อาจเพิ่มมาให้ผ่านมาได้เฉยๆ ([key: string]: any กันพัง)
export interface NotificationPresentation {
  icon?: string; // emoji เดี่ยว ๆ เช่น "📊" — render เป็น text ตรง ๆ ไม่ใช่ผ่าน icon component
  label?: string;
  tone?: string;
  layout?: string;
  bodyStyle?: string;
  primaryActionLabel?: string;
  primaryAction?: NotificationAction;
  [key: string]: any;
}

// resolve label/cta ที่ "ถูกต้องจริง" สำหรับ item จาก feed — backend มาก่อนเสมอ, hardcode ในนี้เป็นแค่
// fallback ตอน backend ยังไม่ส่ง presentation มา (dev เก่า/ยังไม่ deploy) หรือ type ที่ backend ยังไม่รู้จัก
export function resolvePresentation(item: NotificationItem): TypePresentation & { icon?: string } {
  const fallback = getTypePresentation(item.category, item.type);
  const p = item.presentation;
  return {
    icon: p?.icon,
    label: p?.label || fallback.label,
    typeLabel: fallback.typeLabel,
    cta: p?.primaryAction?.label || p?.primaryActionLabel
      || item.primaryAction?.label || item.primaryActionLabel
      || fallback.cta,
  };
}

export interface NotificationState {
  seenAt?: string | null;
  readAt?: string | null;
  clickedAt?: string | null;
  acknowledgedAt?: string | null;
  dismissedAt?: string | null;
}

export interface NotificationAction {
  label: string;
  url: string;
}

export interface NotificationItem {
  id: string;
  category: NotificationCategory;
  type: string;
  title: string;
  body: string;
  bodyTruncated?: boolean;
  image?: string | null;
  imagePlacement?: string;
  deepLink?: string;
  actions?: NotificationAction[];
  // semantic presentation จาก backend (icon/label/tone/layout/bodyStyle) — ดู resolvePresentation()
  presentation?: NotificationPresentation;
  primaryActionLabel?: string;
  primaryAction?: NotificationAction;
  priority?: NotificationPriority;
  acknowledgement?: { required: boolean; label?: string };
  publishedAt?: string;
  expiresAt?: string;
  state?: NotificationState;
}

export interface NotificationAudience {
  type: AudienceType;
  userIds?: string[];
}

export interface AdminNotificationDraft {
  category: NotificationCategory;
  type: string;
  title: string;
  body: string;
  deepLink?: string;
  audience: NotificationAudience;
  channels: NotificationChannel[];
  priority: NotificationPriority;
  retentionDays: number;
  acknowledgement: { required: boolean; label: string };
  image?: string;
  actions?: NotificationAction[];
  locale?: string;
  translations?: Record<string, any>;
  preferencePolicy?: string;
  bypassQuietHours?: boolean;
  expirationAt?: string;
  collapseKey?: string;
}

export interface AdminNotification extends AdminNotificationDraft {
  id: string;
  sourceDomain?: string;
  status: NotificationStatus;
  scheduledAt?: string;
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface NotificationPreferences {
  channels: Record<NotificationChannel, boolean>;
  categories: Record<NotificationCategory, Record<NotificationChannel, boolean>>;
  quietHours: { enabled: boolean; start: string; end: string; timezone: string };
  locale: string;
}

export interface PushSubscriptionRecord {
  id: string;
  device?: string;
  disabled?: boolean;
}

function authHeaders(): HeadersInit {
  const token = getToken();
  if (!token) throw new Error('No token found');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

async function handleError(res: Response, defaultMessage: string): Promise<never> {
  let message = defaultMessage;
  let thMessage = '';
  let code: unknown = '';
  try {
    const data = await res.json();
    const body = data.error && Object.keys(data.error).length > 0 ? data.error : data;
    if (body.th_message) thMessage = body.th_message;
    if (body.message) message = body.message;
    if (body.code !== undefined && body.code !== null && body.code !== '') code = body.code;
  } catch {
    // response body ไม่ใช่ JSON — ใช้ defaultMessage ต่อไป
  }
  handleAuthInvalidToken(code, thMessage || message);
  const error: any = new Error(thMessage || message);
  error.status = res.status;
  error.code = code;
  throw error;
}

async function request<T = any>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) await handleError(res, `Failed request: ${method} ${path}`);
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

const eid = (id: string) => encodeURIComponent(id);

// ---------- User: feed & interactions ----------

export async function getNotifications(options?: {
  limit?: number;
  category?: NotificationCategory | '';
  unread?: boolean;
  cursor?: string | null;
}): Promise<{ items: NotificationItem[]; nextCursor: string | null }> {
  const params = new URLSearchParams({ limit: String(options?.limit ?? 30) });
  if (options?.category) params.set('category', options.category);
  if (options?.unread) params.set('unread', 'true');
  if (options?.cursor) params.set('cursor', options.cursor);
  return request(`/notifications?${params}`, 'GET');
}

export async function getUnreadCount(): Promise<{ count: number }> {
  return request('/notifications/unread-count', 'GET');
}

export async function markNotificationSeen(id: string) {
  return request(`/notifications/${eid(id)}/seen`, 'POST');
}
export async function markNotificationRead(id: string) {
  return request(`/notifications/${eid(id)}/read`, 'POST');
}
export async function markNotificationUnread(id: string) {
  return request(`/notifications/${eid(id)}/unread`, 'POST');
}
export async function markNotificationClicked(id: string) {
  return request(`/notifications/${eid(id)}/click`, 'POST');
}
export async function acknowledgeNotification(id: string) {
  return request(`/notifications/${eid(id)}/acknowledge`, 'POST');
}
export async function dismissNotification(id: string) {
  return request(`/notifications/${eid(id)}/dismiss`, 'POST');
}
export async function markAllNotificationsRead(): Promise<{ changed: number }> {
  return request('/notifications/read-all', 'POST');
}

// ---------- User: preferences & channels ----------

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return request('/notifications/preferences', 'GET');
}
export async function updateNotificationPreferences(payload: NotificationPreferences) {
  return request('/notifications/preferences', 'PATCH', payload);
}
export async function getNotificationChannelsStatus(): Promise<Record<string, any>> {
  return request('/notifications/channels', 'GET');
}

// ---------- User: Web Push ----------

export async function getPushConfig(): Promise<{ available: boolean; publicKey?: string }> {
  return request('/notifications/push/config', 'GET');
}
export async function getPushSubscriptions(): Promise<PushSubscriptionRecord[]> {
  return request('/notifications/push/subscriptions', 'GET');
}
export async function registerPushSubscription(payload: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  device: string;
}): Promise<PushSubscriptionRecord[]> {
  return request('/notifications/push/subscriptions', 'POST', payload);
}
export async function deletePushSubscription(id: string) {
  return request(`/notifications/push/subscriptions/${eid(id)}`, 'DELETE');
}

// SSE ต้องใส่ Authorization header เอง (EventSource ทำไม่ได้) จึงใช้ fetch + stream reader แทน
export async function openNotificationStream(signal: AbortSignal): Promise<Response> {
  const token = getToken();
  if (!token) throw new Error('No token found');
  return fetch(`${API_URL}/notifications/stream`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
    signal,
    cache: 'no-store',
  });
}

// ---------- Admin: compose / lifecycle ----------

export async function createAdminNotification(payload: AdminNotificationDraft): Promise<AdminNotification> {
  return request('/admin/notifications', 'POST', payload);
}
export async function updateAdminNotification(id: string, payload: AdminNotificationDraft): Promise<AdminNotification> {
  return request(`/admin/notifications/${eid(id)}`, 'PUT', payload);
}
export async function getAdminNotification(id: string): Promise<AdminNotification> {
  return request(`/admin/notifications/${eid(id)}`, 'GET');
}
export async function listAdminNotifications(options?: {
  status?: NotificationStatus | '';
  category?: NotificationCategory | '';
  priority?: NotificationPriority | '';
  cursor?: string | null;
  limit?: number;
}): Promise<{ items: AdminNotification[]; nextCursor: string | null }> {
  const params = new URLSearchParams({ limit: String(options?.limit ?? 30) });
  if (options?.status) params.set('status', options.status);
  if (options?.category) params.set('category', options.category);
  if (options?.priority) params.set('priority', options.priority);
  if (options?.cursor) params.set('cursor', options.cursor);
  return request(`/admin/notifications?${params}`, 'GET');
}
export async function previewAdminNotification(id: string): Promise<any> {
  return request(`/admin/notifications/${eid(id)}/preview`, 'GET');
}
export async function testAdminNotification(id: string): Promise<AdminNotification> {
  return request(`/admin/notifications/${eid(id)}/test`, 'POST');
}
export async function publishAdminNotification(id: string): Promise<AdminNotification> {
  return request(`/admin/notifications/${eid(id)}/publish`, 'POST');
}
export async function scheduleAdminNotification(id: string, scheduledAt: string): Promise<AdminNotification> {
  return request(`/admin/notifications/${eid(id)}/schedule`, 'POST', { scheduledAt });
}
export async function cancelAdminNotification(id: string): Promise<AdminNotification> {
  return request(`/admin/notifications/${eid(id)}/cancel`, 'POST');
}
export async function expireAdminNotification(id: string): Promise<AdminNotification> {
  return request(`/admin/notifications/${eid(id)}/expire`, 'POST');
}

// ---------- Admin: statistics ----------

export async function getAdminNotificationsAggregateStatistics(): Promise<any> {
  return request('/admin/notifications/statistics', 'GET');
}
export async function getAdminNotificationStatistics(id: string): Promise<any> {
  return request(`/admin/notifications/${eid(id)}/statistics`, 'GET');
}
export async function getAdminNotificationDeliveries(id: string, cursor?: string | null): Promise<any[]> {
  return request(`/admin/notifications/${eid(id)}/deliveries${cursor ? `?cursor=${eid(cursor)}` : ''}`, 'GET');
}
export async function getAdminNotificationAudit(id: string): Promise<any[]> {
  return request(`/admin/notifications/${eid(id)}/audit`, 'GET');
}
